<template>
  <Loading :loading="loading">
    <template>
      <span>
        <div v-if="itemsFiltered.length" class="parvandeh-reports-list">
          <div v-for="(item, index) in itemsFiltered" :key="index">
            <fade-transition :delay="300">
              <b-row v-if="!item.deleted" class="margin-0">
                <b-col cols="12" class="parvandeh-reports-list__item">
                  <div class="parvandeh-reports-list__top-line">
                    <div>
                      <span class="parvandeh-reports-list__title">
                        {{ $lang.report_name }}:
                      </span>
                      <span class="parvandeh-reports-list__value">
                        {{ item.caption }}
                      </span>
                    </div>
                    <MenuPopupWrapper
                      iconClass="parvandeh-reports-list__icon"
                      menuClass="left-0 top-12 z-index-1 padding-top-24"
                      :items="itemButtons"
                      :permissions="permissions"
                      :showMenu="itemMenuId === item.id"
                      :rowItem="item"
                      @click="showItemMenu"
                      @close="closeItemMenu"
                    />
                  </div>
                  <div class="parvandeh-reports-list__line">
                    <span class="parvandeh-reports-list__title">
                      {{ $lang.resource_report }}:
                    </span>
                    <span class="parvandeh-reports-list__value">
                      {{ item.resource }}
                    </span>
                  </div>
                  <div class="parvandeh-reports-list__line">
                    <span class="parvandeh-reports-list__title">
                      {{ $lang.created_at }}:
                    </span>
                    <span
                      class="parvandeh-reports-list__value"
                      v-html="item.createdAt"
                    >
                    </span>
                  </div>
                  <div class="parvandeh-reports-list__line">
                    <span class="parvandeh-reports-list__title">
                      {{ $lang.file_type }}:
                    </span>
                    <span class="parvandeh-reports-list__value">
                      {{ item.format }}
                    </span>
                  </div>
                  <div
                    v-if="item.previewImage"
                    class="parvandeh-reports-list__line"
                  >
                    <b-img-lazy
                      class="width-231 height-256 object-fit--cover padding-4 border--solid border--complementary-50 border-radius-8"
                      :src="item.previewImage"
                      :onerror="`this.src='${defaultImage}';this.classList.add('padding-4')`"
                    />
                  </div>
                </b-col>
              </b-row>
            </fade-transition>
          </div>
        </div>
        <div
          v-else
          class="height-percent-100 flex-center flex-direction-column"
        >
          <b-img-lazy
            class="margin-bottom-16"
            :src="$config.images.shared.redesign.empty.reportList"
          />
          <div class="wrapper-list__empty-text">
            {{ $lang.report_list_empty_text }}
          </div>
        </div>
        <Modal
          :show="deleteReportConfirmModal"
          :title="$lang.delete"
          :closeOnBackdrop="false"
          :hasClose="true"
          :hasFooter="true"
          size="sm"
          :okButton="true"
          okButtonVariant="filled-error"
          :okButtonText="$lang.delete"
          :okLoading="deleteReportConfirmLoading"
          :ok="removeReport"
          @close="closeDeleteReportConfirmModal"
        >
          <template #body>
            <div class="margin-bottom-24 font-size-14 color-complementary-300">
              {{ $lang.remove_sure_message.replace("{0}", $lang.report_label) }}
            </div>
          </template>
        </Modal>
      </span>
    </template>
  </Loading>
</template>

<script>
import MenuPopupWrapper from "@/components/re-design/modal/MenuPopupWrapper.vue";
import Modal from "@/components/re-design/modal/Modal.vue";
import Loading from "./Loading.vue";
import { pathOr, includes } from "ramda";
import {
  createDateFormat,
  checkPermission,
  goToRoute,
  generateConfig,
} from "@/shared/utils";
import { getParvandehReportList, deleteReport } from "@/services";

export default {
  components: { MenuPopupWrapper, Modal, Loading },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    code: {
      type: [String, Number],
      default: null,
    },
    resource: {
      type: String,
      default: null,
    },
    resourceId: {
      type: [String, Number],
      default: null,
    },
    saltFileName: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      items: [],
      permissions: [],
      itemMenuId: null,
      showDeleteButton: false,
      requestType: null,
      deleteReportConfirmModal: false,
      deleteReportConfirmLoading: false,
      currentItemId: null,
      deletedItemsId: [],
    };
  },
  computed: {
    itemsFiltered() {
      return this.items.map((item) => {
        const resourceCaption = pathOr("", ["resource", "caption"], item);
        const resourceId = pathOr("", ["resource", "id"], item);
        const id = pathOr(null, ["report_detail_id"], item);
        const deleted = includes(id, this.deletedItemsId);
        return {
          id,
          isManual: pathOr(0, ["is_manual"], item),
          requestType: pathOr(null, ["request_type"], item),
          downloadLink: pathOr(null, ["download_link"], item),
          reportName: pathOr(null, ["report_name"], item),
          caption: pathOr("", ["report_caption"], item),
          resource: `${resourceCaption} ${this.$lang.the_number} ${resourceId}`,
          createdAt: createDateFormat(
            pathOr("", ["created_at", "jalali"], item)
          ),
          format: pathOr("", ["format"], item),
          pageName: pathOr(null, ["front_page_name"], item),
          parvandehCode: pathOr(null, ["parvandeh", "code"], item),
          resourceName: pathOr(null, ["resource", "name"], item),
          resourceCode: pathOr(null, ["resource", "code"], item),
          examPackId: pathOr(null, ["exam_pack", "id"], item),
          previewImage: pathOr("", ["preview_image"], item),
          azmoonExecutePersonCode: pathOr(
            null,
            ["azmoon_execute_person_code"],
            item
          ),
          deleted,
        };
      });
    },
    itemButtons() {
      return [
        {
          caption: this.$lang.view,
          icon: "eye-outline",
          show: this.requestType === "service",
          permissions: ["admin", "edit", "view"],
          loading: false,
          click: this.viewReport,
        },
        {
          caption: this.$lang.download,
          icon: "file-download-outline",
          show: this.requestType === "file",
          permissions: ["admin", "edit", "view"],
          loading: false,
          click: this.downloadReport,
        },
        {
          caption: this.$lang.remove,
          icon: "trash-can-outline",
          show: this.showDeleteButton,
          permissions: ["admin"],
          loading: false,
          click: this.showDeleteReportConfirmModal,
        },
      ];
    },
    defaultImage() {
      return generateConfig(["images", "shared", "noImage"]);
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        value && this.getItems();
      },
    },
  },
  methods: {
    pathOr,
    includes,
    createDateFormat,
    checkPermission,
    goToRoute,
    close() {
      this.$emit("close");
    },
    async getItems() {
      try {
        this.items = [];
        this.show = true;
        this.loading = true;
        const response = await getParvandehReportList(
          this.code,
          this.resource,
          this.resourceId
        );
        const { items, permissions } = response;
        this.items = items;
        this.permissions = permissions;
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.loading = false;
      }
    },
    showItemMenu(item) {
      this.showDeleteButton = item.isManual === 1;
      this.requestType = item.requestType;
      const id = pathOr(null, ["id"], item);
      this.itemMenuId = this.itemMenuId === id ? null : id;
    },
    closeItemMenu() {
      this.itemMenuId = null;
    },
    showDeleteReportConfirmModal(item) {
      this.currentItemId = item.id;
      this.deleteReportConfirmModal = true;
    },
    closeDeleteReportConfirmModal() {
      this.currentItemId = null;
      this.deleteReportConfirmModal = false;
    },
    viewReport(item) {
      const isCenterExecute = item.resourceName === "center_execute";
      const resourceCode = isCenterExecute
        ? item.resourceCode // use in kanoon module
        : item.azmoonExecutePersonCode; // use in azmoon module

      const name = `${item.pageName}-report`;
      const params = {
        parvandehCode: item.parvandehCode,
        examPackId: item.examPackId, // use in azmoon module
        resourceCode,
      };
      goToRoute(name, params);
    },
    downloadReport(item) {
      const url = item.downloadLink;
      if (!url) return;
      const saltName = this.saltFileName ? `${this.saltFileName}_` : "";
      const fileName = `${saltName}${item.reportName}`;
      const generateUrl = `${url}?file_name=${fileName}`;
      window.open(generateUrl, "_blank");
    },
    async removeReport() {
      try {
        this.deleteReportConfirmLoading = true;
        const response = await deleteReport(this.currentItemId);
        const { message } = response;
        this.$notify.success(message);
        this.deletedItemsId.push(this.currentItemId);
        this.closeDeleteReportConfirmModal();
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.deleteReportConfirmLoading = false;
      }
    },
  },
};
</script>
